import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { SelectionMarkColor } from './selection-mark-color.model';

dayjs.extend(customParseFormat);

export function parseBirthDate(dateStr: string | null | undefined): dayjs.Dayjs | null {
  const raw = dateStr?.trim();
  if (!raw) {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const d = dayjs(raw.slice(0, 10));
    return d.isValid() ? d.startOf('day') : null;
  }

  const dmy = dayjs(raw, 'DD-MM-YYYY', true);
  if (dmy.isValid()) {
    return dmy.startOf('day');
  }

  const d = dayjs(raw);
  return d.isValid() ? d.startOf('day') : null;
}

export function matchesBirthDateRange(
  dateOfBirth: string | null | undefined,
  minBirthDate: string | null | undefined,
  maxBirthDate: string | null | undefined
): boolean {
  const birth = parseBirthDate(dateOfBirth);
  const min = parseBirthDate(minBirthDate);
  const max = parseBirthDate(maxBirthDate);
  if (!birth || (!min && !max)) {
    return false;
  }

  if (min && birth.isBefore(min, 'day')) {
    return false;
  }
  if (max && birth.isAfter(max, 'day')) {
    return false;
  }

  return true;
}

export function isBeforeBirthDateRange(
  dateOfBirth: string | null | undefined,
  minBirthDate: string | null | undefined,
  _maxBirthDate?: string | null | undefined
): boolean {
  const birth = parseBirthDate(dateOfBirth);
  const min = parseBirthDate(minBirthDate);
  if (!birth || !min) {
    return false;
  }

  return birth.isBefore(min, 'day');
}

export function isAfterBirthDateRange(
  dateOfBirth: string | null | undefined,
  _minBirthDate: string | null | undefined,
  maxBirthDate: string | null | undefined
): boolean {
  const birth = parseBirthDate(dateOfBirth);
  const max = parseBirthDate(maxBirthDate);
  if (!birth || !max) {
    return false;
  }

  return birth.isAfter(max, 'day');
}

export function suggestMarkColorByBirthDate(
  dateOfBirth: string | null | undefined,
  colors: SelectionMarkColor[]
): string | null {
  const match = (colors || [])
    .filter((c) => c.used !== false)
    .find((c) =>
      (c.min_birth_date || c.max_birth_date) &&
      matchesBirthDateRange(dateOfBirth, c.min_birth_date, c.max_birth_date)
    );

  return match?.value ?? null;
}

export function filterAnimalsByMarkColor<
  T extends { date_of_birth?: string | null; selection_mark_color?: string | null },
>(
  animals: T[],
  colorValue: string,
  colors: SelectionMarkColor[]
): T[] {
  const band = colors.find((c) => c.value === colorValue);
  if (band && (band.min_birth_date || band.max_birth_date)) {
    return animals.filter((a) =>
      matchesBirthDateRange(a.date_of_birth, band.min_birth_date, band.max_birth_date)
    );
  }

  return animals.filter((a) => a.selection_mark_color === colorValue);
}

export function getMarkColorFilterTitle(c: SelectionMarkColor): string {
  if (c.min_birth_date && c.max_birth_date) {
    const min = dayjs(c.min_birth_date).format('D MMM YYYY');
    const max = dayjs(c.max_birth_date).format('D MMM YYYY');
    return `${c.name} (${min} – ${max})`;
  }
  if (c.min_birth_date) {
    return `${c.name} (van ${dayjs(c.min_birth_date).format('D MMM YYYY')})`;
  }
  if (c.max_birth_date) {
    return `${c.name} (tot ${dayjs(c.max_birth_date).format('D MMM YYYY')})`;
  }

  return c.name;
}

/** Zet opgeslagen geboortedatum-range om naar dagen t.o.v. referentiedatum (voor bewerk-formulier). */
export function birthDateRangeToAgeDays(
  referenceDate: string,
  minBirthDate: string,
  maxBirthDate: string
): { min_age: number; max_age: number } {
  const ref = dayjs(referenceDate).startOf('day');
  return {
    min_age: ref.diff(dayjs(maxBirthDate).startOf('day'), 'day'),
    max_age: ref.diff(dayjs(minBirthDate).startOf('day'), 'day'),
  };
}

export function formatSelectionBirthDateRange(
  minBirthDate?: string | null,
  maxBirthDate?: string | null
): string {
  if (minBirthDate && maxBirthDate) {
    return `${dayjs(minBirthDate).format('D MMM YYYY')} – ${dayjs(maxBirthDate).format('D MMM YYYY')}`;
  }
  if (minBirthDate) {
    return `van ${dayjs(minBirthDate).format('D MMM YYYY')}`;
  }
  if (maxBirthDate) {
    return `tot ${dayjs(maxBirthDate).format('D MMM YYYY')}`;
  }

  return '—';
}

/** Waarde voor HTML date-input (YYYY-MM-DD). */
export function toDateInputValue(dateStr: string | null | undefined): string | null {
  const d = parseBirthDate(dateStr);
  return d ? d.format('YYYY-MM-DD') : null;
}

/** Leeftijd in dagen t.o.v. vandaag (alleen ter info in UI). */
export function ageInDaysFromBirthDateToToday(dateStr: string | null | undefined): number | null {
  const birth = parseBirthDate(dateStr);
  if (!birth) {
    return null;
  }
  return Math.max(0, dayjs().startOf('day').diff(birth, 'day'));
}

/** Referentiedatum voor leeftijd bij aanmaak (created_at van de lijst, of vandaag). */
export function resolveBirthDateReference(createdAt?: string | null): string {
  const parsed = toDateInputValue(createdAt);
  return parsed ?? dayjs().format('YYYY-MM-DD');
}

/** Leeftijd in dagen t.o.v. een vaste referentiedatum (bijv. aanmaakdatum lijst). */
export function formatAgeRangeAtReferenceHint(
  minBirthDate?: string | null,
  maxBirthDate?: string | null,
  referenceDate?: string | null
): string | null {
  if (!minBirthDate || !maxBirthDate || !referenceDate) {
    return null;
  }

  const ages = birthDateRangeToAgeDays(referenceDate, minBirthDate, maxBirthDate);
  const low = Math.min(ages.min_age, ages.max_age);
  const high = Math.max(ages.min_age, ages.max_age);
  return `Leeftijd bij aanmaak: ${low}–${high} dagen`;
}

/** Toont welke leeftijd (nu) bij een geboortedatum-range hoort. */
export function formatCurrentAgeRangeHint(
  minBirthDate?: string | null,
  maxBirthDate?: string | null
): string | null {
  if (!minBirthDate || !maxBirthDate) {
    return null;
  }
  const youngest = ageInDaysFromBirthDateToToday(maxBirthDate);
  const oldest = ageInDaysFromBirthDateToToday(minBirthDate);
  if (youngest === null || oldest === null) {
    return null;
  }
  const low = Math.min(youngest, oldest);
  const high = Math.max(youngest, oldest);
  return `Leeftijd nu: ${low}–${high} dagen`;
}

/** Leeftijd bij aanmaak én nu (voor hints onder datumvelden). */
export function formatAgeRangeHints(
  minBirthDate?: string | null,
  maxBirthDate?: string | null,
  referenceDate?: string | null
): string[] {
  if (!minBirthDate || !maxBirthDate) {
    return [];
  }

  const ref = referenceDate ?? dayjs().format('YYYY-MM-DD');
  const atCreation = formatAgeRangeAtReferenceHint(minBirthDate, maxBirthDate, ref);
  const now = formatCurrentAgeRangeHint(minBirthDate, maxBirthDate);
  const hints: string[] = [];

  if (atCreation) {
    hints.push(atCreation);
  }
  if (now && now !== atCreation) {
    hints.push(now);
  }

  return hints;
}

export function isValidBirthDateRange(
  minBirthDate?: string | null,
  maxBirthDate?: string | null
): boolean {
  const min = parseBirthDate(minBirthDate);
  const max = parseBirthDate(maxBirthDate);
  if (!min || !max) {
    return true;
  }
  return !min.isAfter(max, 'day');
}
